import { Button, Popconfirm, Table, Tooltip } from 'antd';
import TextArea from 'antd/lib/input/TextArea';
import type { ColumnsType } from 'antd/lib/table';
import { format } from 'date-fns';
import { useState } from 'react';
import { Optional } from '../../Shared';
import { archiveStatusSchema, ArchiveStatus } from '../types';

type TeamTableRow = {
  readonly key: number;
  readonly name: string;
  readonly date: string | undefined;
  readonly user: string | undefined;
};

// TODO: Maybe? Ask RFS about reason validation rules
// Maybe this should be a select box with a list of reasons
// and an input box for "Other" reasons?
const reasonIsInvalid = (reason: string) => reason === '';

const tableColumns: ColumnsType<TeamTableRow> = [
  { title: 'Event', dataIndex: 'name', key: 'name' },
  { title: 'Date', dataIndex: 'date', key: 'date' },
  { title: 'User', dataIndex: 'user', key: 'user' },
  { title: 'Reason', dataIndex: 'reason', key: 'reason' },
];

type Event = {
  readonly name: string;
  readonly date: number;
  readonly user: string | undefined;
  readonly reason: string | undefined;
};

export type TeamHistoryProps = {
  readonly teamName: string;
  readonly events: readonly Event[];
  readonly allowChangeStatus: boolean;
  readonly status: ArchiveStatus;
} & {
  readonly onReactivate: (reason: string) => Promise<void>;
};

const TeamHistory = (props: TeamHistoryProps) => {
  const [reason, setReason] = useState<string>('');

  // Sort and format (June 22nd, 2023) the event dates
  const tableRows: readonly TeamTableRow[] = props.events
    .map((event, i) => ({ key: i, ...event }))
    .sort((a, b) => a.date - b.date)
    .map(({ date, ...row }) => ({
      ...row,
      date: format(new Date(date), 'PPP'),
    }));

  return (
    <div>
      <Table
        id="archived-teams-table"
        dataSource={tableRows}
        columns={tableColumns}
        pagination={false}
        style={{ marginBottom: '10px' }}
        scroll={{ y: 240 }}
      />

      <Optional
        show={
          // Only show the reactivate button if the team is archived
          // and the user has permission to reactivate it
          props.allowChangeStatus &&
          props.status === archiveStatusSchema.Values.Archived
        }
      >
        <div style={{ textAlign: 'right' }}>
          <Popconfirm
            placement="topRight"
            title={
              <div style={{ display: 'flex' }}>
                <p>Why are you reactivating this team?</p>
                <TextArea
                  rows={4}
                  placeholder="The incident..."
                  value={reason}
                  onChange={(e) => setReason(e.currentTarget.value)}
                  style={{ resize: 'none', marginBottom: 10, marginLeft: 10 }}
                  maxLength={1000}
                  showCount
                />
              </div>
            }
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onConfirm={() => props.onReactivate(reason)}
            okButtonProps={{ disabled: reasonIsInvalid(reason) }}
            onCancel={() => setReason('')}
            onVisibleChange={() => setReason('')}
            okText="Confirm Reactivate"
            cancelText="Cancel"
          >
            <Tooltip title={`Reactivate ${props.teamName}`}>
              <Button type="primary">Reactivate</Button>
            </Tooltip>
          </Popconfirm>
        </div>
      </Optional>
    </div>
  );
};

export default TeamHistory;
